import { Button, cn, Select, SelectItem, Spinner, Textarea } from '@heroui/react'
import { generateKeypair, getCurrentToken } from '@qse/api'
import {
  KEY_TYPES,
  KeyType,
  RsaKeysGenerationFailedPayload,
  RsaKeysReadyPayload,
  UserRoleEnum,
} from '@qse/constants'
import { ModelControlEnum } from '@qse/constants'
import { useRealtime } from '@qse/realtime'
import { useUserStore } from '@qse/stores'
import { ModelControl, PaymentOverdueBanner } from '@qse/ui'
import { AlertTriangle, CopyIcon, DownloadIcon } from 'lucide-react'
import React, { useCallback, useState } from 'react'

import { useKeyProtectionContext } from '@/contexts/key-protection-context'
import { canConnectRealtime, useKeyProtection, useRouteContext } from '@/hooks'

interface KeyDisplayProps {
  title: string
  value: string
  isGenerating: boolean
  onCopy: (text: string, keyType: 'public' | 'private' | 'symmetric') => void
  onDownload: (text: string, keyType: 'public' | 'private' | 'symmetric') => void
  keyType: 'public' | 'private' | 'symmetric'
  copiedKey: 'public' | 'private' | 'symmetric' | null
  errorMessage?: string
  keyTypeLabel?: string
}

const KeyDisplay: React.FC<KeyDisplayProps> = ({
  title,
  value,
  isGenerating,
  onCopy,
  onDownload,
  keyType,
  copiedKey,
  errorMessage,
  keyTypeLabel,
}) => {
  const isKeysGenerated = value.length > 0

  return (
    <div>
      <div>
        <span className="font-medium">{title}</span>
        {keyTypeLabel && (
          <span className="ml-2 px-2 py-0.5 text-xs rounded bg-neutral-800 text-neutral-300">
            {keyTypeLabel}
          </span>
        )}
      </div>
      <div className="relative mt-2">
        <Textarea
          readOnly
          errorMessage={errorMessage}
          isInvalid={!!errorMessage}
          minRows={4}
          placeholder={isGenerating ? 'Generating...' : `${title} will appear here`}
          radius="sm"
          value={value}
          variant="bordered"
          classNames={{
            label: [
              'text-foreground',
              'dark:text-foreground',
              'group-data-[filled-within=true]:text-white',
              'dark:group-data-[filled-within=true]:text-white',
              'group-data-[invalid=true]:text-danger',
              'dark:group-data-[invalid=true]:text-danger',
            ],
            input: [
              'bg-transparent',
              'text-gray-900 dark:text-white',
              'placeholder:text-gray-500 dark:placeholder:text-white/60 group-data-[invalid=true]:placeholder:text-danger',
            ],
            innerWrapper: 'bg-transparent',
            inputWrapper: [
              'text-gray-900 dark:!text-white',
              'bg-gray-50 dark:bg-[#1e1e20]',
              'border-gray-200 dark:border-default-600',
              'hover:bg-gray-100 dark:hover:bg-[#1e1e20]/70',
              'group-data-[focus=true]:bg-gray-100 dark:group-data-[focus=true]:bg-[#1e1e20]/50',
              'group-data-[focus=true]:border-primary dark:group-data-[focus=true]:border-primary',
              '!cursor-text',
            ],
          }}
        />
        {isKeysGenerated && (
          <div className="absolute top-2 right-2 z-40 flex gap-2">
            <Button
              className=""
              disabled={!value}
              radius="sm"
              size="sm"
              startContent={<CopyIcon className="w-[18px]" color="currentColor" size={18} />}
              onPress={() => onCopy(value, keyType)}
            >
              {copiedKey === keyType ? 'Copied!' : 'Copy'}
            </Button>
            <Button
              className=""
              disabled={!value}
              radius="sm"
              size="sm"
              startContent={<DownloadIcon className="w-[18px]" color="currentColor" size={18} />}
              onPress={() => onDownload(value, keyType)}
            >
              Download
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

const GenerateKeysScreen = () => {
  const [selectedKeyType, setSelectedKeyType] = useState<KeyType>(KEY_TYPES.RSA_4096)
  const [copiedKey, setCopiedKey] = useState<'public' | 'private' | 'symmetric' | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedKeys, setGeneratedKeys] = useState<{
    publicKey: string
    privateKey: string
    symmetricKey: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [openQuotaExceededModal, setOpenQuotaExceededModal] = useState(false)

  const { user } = useUserStore()
  const isExceeded = user?.drive?.quotaSummary?.service.status === 'EXCEEDED'
  const routeContext = useRouteContext()
  const { incrementApiQuota } = useUserStore()

  // WebSocket handlers for RSA key generation events
  const handleRsaKeysReady = useCallback(
    (payload: RsaKeysReadyPayload) => {
      console.log('🔑 RSA keys received')
      setGeneratedKeys({
        publicKey: payload.keys.public,
        privateKey: payload.keys.private,
        symmetricKey: '',
      })
      setIsGenerating(false)
      setError(null)
      // Only increment quota after successful RSA key generation
      incrementApiQuota(1)
    },
    [incrementApiQuota],
  )

  const handleRsaKeysGenerationFailed = useCallback((payload: RsaKeysGenerationFailedPayload) => {
    console.error('❌ RSA keys generation failed:', payload)
    setError(payload.error || 'Key generation failed')
    setIsGenerating(false)
    setGeneratedKeys(null)
    disableAppProtection()
    disableBrowserProtection()
  }, [])

  // Set up WebSocket connection with event handlers
  useRealtime({
    getToken: getCurrentToken,
    shouldConnect: () => canConnectRealtime(user?.userRole),
    getRouteContext: () => routeContext,
    eventHandlers: {
      onRsaKeysReady: handleRsaKeysReady,
      onRsaKeysGenerationFailed: handleRsaKeysGenerationFailed,
    },
  })

  // Context for app-level protection
  const { enableProtection: enableAppProtection, disableProtection: disableAppProtection } =
    useKeyProtectionContext()

  // Hook for browser-level protection
  const {
    isProtected,
    enableProtection: enableBrowserProtection,
    disableProtection: disableBrowserProtection,
    showKeyLossWarning,
  } = useKeyProtection({
    onBeforeUnload: () => {
      console.log('User left page with generated keys - quota will be deducted')
    },
    onProtectionDisabled: () => {
      console.log('Key protection disabled - keys were copied')
    },
  })

  const handleCopy = async (text: string, keyType: 'public' | 'private' | 'symmetric') => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedKey(keyType)
      // Reset copied state after 2 seconds
      setTimeout(() => setCopiedKey(null), 2000)

      // Disable protection from both levels when keys are copied
      disableAppProtection()
      disableBrowserProtection()
    } catch (error) {
      console.error('Failed to copy text:', error)
    }
  }

  const handleDownload = (text: string, keyType: 'public' | 'private' | 'symmetric') => {
    const isRSA = selectedKeyType === KEY_TYPES.RSA_4096
    const fileExtension = isRSA ? 'pem' : 'txt'
    const fileName = isRSA
      ? `rsa-${keyType}-key.${fileExtension}`
      : `aes-256-symmetric-key.${fileExtension}`

    const blob = new Blob([text], { type: 'application/x-pem-file' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')

    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    // Disable protection from both levels when keys are downloaded
    disableAppProtection()
    disableBrowserProtection()
  }

  // Keys are shown one time only, so any un-saved keys still on screen are lost
  // the moment a new generation starts (and their quota charge is wasted)
  const hasUnsavedKeys = isProtected && generatedKeys !== null

  const startKeyGeneration = async () => {
    try {
      // Reset previous state and clear any existing keys
      setError(null)
      setGeneratedKeys(null)
      setIsGenerating(true)

      enableAppProtection()
      enableBrowserProtection()

      if (selectedKeyType === KEY_TYPES.AES_256) {
        // AES-256 key generation is synchronous
        const response = await generateKeypair({ keyType: selectedKeyType })

        console.log('🔑 AES-256 key generated')

        // Handle AES key response
        if ('key' in response) {
          setGeneratedKeys({
            publicKey: '',
            privateKey: '',
            symmetricKey: response.key,
          })
          setIsGenerating(false)
          // Only increment quota after successful AES key generation
          incrementApiQuota(1)
        } else {
          throw new Error('Invalid AES key response')
        }
      } else {
        // RSA-4096 key generation is asynchronous via WebSocket
        await generateKeypair({ keyType: selectedKeyType })
        console.log('🔑 RSA key generation request sent, waiting for WebSocket events...')
        // Note: Quota will be incremented in handleRsaKeysReady when keys are successfully received
      }
    } catch (error) {
      console.error('Failed to start key generation:', error)
      setError('Failed to start key generation')
      setIsGenerating(false)

      // Always disable protection when there's an error
      disableAppProtection()
      disableBrowserProtection()

      // If the request fails, we might want to decrement the quota back
      // but for now we'll leave it as is since the quota was consumed by the attempt
    }
  }

  const handleGenerate = () => {
    if (isExceeded) {
      setOpenQuotaExceededModal(true)

      return
    }

    if (hasUnsavedKeys) {
      showKeyLossWarning({
        message:
          'The current keys will be lost if you generate new ones. Are you sure you want to continue?',
        proceedButtonText: 'Generate New Keys',
        onProceed: () => {
          startKeyGeneration()
        },
      })

      return
    }

    startKeyGeneration()
  }

  const isRSA = selectedKeyType === KEY_TYPES.RSA_4096
  const isAES = selectedKeyType === KEY_TYPES.AES_256

  const isTenantAdmin = user?.userRole === UserRoleEnum.TENANT_ADMIN

  return (
    <div className="lg:pb-4 pb-40 md:px-[30px] px-4 w-full">
      {isTenantAdmin && (
        <div className="sticky md:top-24 top-20 w-full h-full pb-1 bg-black z-50">
          <PaymentOverdueBanner isInView={false} />
        </div>
      )}
      <div className="mx-auto">
        <h1 className="text-2xl font-semibold mb-1">Generate Keys</h1>

        {/* Model Control */}
        <ModelControl
          control={ModelControlEnum.QUOTA_EXCEEDED}
          data={{}}
          reset={() => {}}
          setModelActive={() => {}}
          setToggleModel={setOpenQuotaExceededModal}
          toggleModel={openQuotaExceededModal}
          onClose={() => setOpenQuotaExceededModal(false)}
        />

        {/* Key Type Selection */}
        <div className="bg-white dark:bg-zinc-900 rounded-lg p-6 mb-8 shadow w-full border border-gray-200 dark:border-zinc-800">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Key Type
            </label>
            <Select
              className="max-w-xs"
              isDisabled={isGenerating}
              radius="sm"
              selectedKeys={[selectedKeyType]}
              variant="bordered"
              onSelectionChange={keys => {
                const selected = Array.from(keys)[0] as KeyType

                if (!selected || selected === selectedKeyType) {
                  return
                }

                const applyKeyTypeSwitch = () => {
                  setSelectedKeyType(selected)
                  // Clear any existing keys when switching types
                  setGeneratedKeys(null)
                  setError(null)
                }

                if (hasUnsavedKeys) {
                  showKeyLossWarning({
                    message:
                      'The generated keys will be lost if you switch the key type. Are you sure you want to continue?',
                    proceedButtonText: 'Switch Key Type',
                    onProceed: () => {
                      disableAppProtection()
                      disableBrowserProtection()
                      applyKeyTypeSwitch()
                    },
                  })

                  return
                }

                applyKeyTypeSwitch()
              }}
            >
              <SelectItem key={KEY_TYPES.RSA_4096}>RSA-4096</SelectItem>
              <SelectItem key={KEY_TYPES.AES_256}>AES-256</SelectItem>
            </Select>
          </div>

          {/* Key Type Description */}
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {isRSA && (
              <p>
                <strong>RSA-4096:</strong> Asymmetric keypair generation using quantum-safe entropy
                sources.
              </p>
            )}
            {isAES && (
              <p>
                <strong>AES-256:</strong> Symmetric key generation using quantum-safe entropy
                sources. Keys are generated immediately and displayed in hexadecimal format.
              </p>
            )}
          </div>
        </div>

        {/* Loading indicator with progress message */}
        {isGenerating ? (
          <div className="bg-blue-50 mb-8 dark:bg-[#2196F31A] rounded-[10px] p-4 flex items-center gap-4 border-blue-200 dark:border-[#2196F3] border-1 mt-4">
            <Spinner color="primary" size="lg" />
            <div>
              <p className="font-medium text-blue-800 dark:text-blue-200">
                {isRSA ? 'Generating RSA-4096 keys...' : 'Generating AES-256 key...'}
              </p>
              <p className="text-sm text-blue-600 dark:text-blue-300">
                Key generation is in progress. Please wait for completion.
              </p>
            </div>
          </div>
        ) : isExceeded ? (
          <div
            className={cn(
              'border border-red-200 mb-8 bg-red-500/10 dark:border-red-800 rounded-lg shadow-sm transform transition-all duration-300 ease-in-out',
              'translate-y-0 opacity-100',
            )}
          >
            <div className="px-4 py-3">
              <div className="flex flex-row gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-red-500 dark:text-[#DD524C]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-900 dark:text-red-100 ">
                      User quota exceeded - key generation is disabled until quota resets
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-blue-50 dark:bg-[#2196F31A] mb-8 rounded-[10px] p-[10px] flex items-center gap-2 border-blue-200 dark:border-[#2196F3] border-1">
            <img alt="info" className="w-14" src={'/assets/svg/warning.svg'} />
            <p>
              <span className="font-bold">ONE TIME ONLY:</span> This is your only chance to view and
              save your {isRSA ? 'keypair' : 'key'}. It will not be shown again for security reasons
            </p>
          </div>
        )}

        <div className="bg-white dark:bg-zinc-900 rounded-lg flex flex-col gap-4 p-6 mb-8 shadow w-full border border-gray-200 dark:border-zinc-800">
          {isRSA ? (
            // RSA Key Display (Public and Private)
            <>
              <KeyDisplay
                copiedKey={copiedKey}
                errorMessage={error || undefined}
                isGenerating={isGenerating}
                keyType="public"
                keyTypeLabel="RSA-4096"
                title="Public Key"
                value={generatedKeys?.publicKey || ''}
                onCopy={handleCopy}
                onDownload={handleDownload}
              />

              <KeyDisplay
                copiedKey={copiedKey}
                errorMessage={error || undefined}
                isGenerating={isGenerating}
                keyType="private"
                keyTypeLabel="RSA-4096"
                title="Private Key"
                value={generatedKeys?.privateKey || ''}
                onCopy={handleCopy}
                onDownload={handleDownload}
              />
            </>
          ) : (
            // AES Key Display (Single Symmetric Key)
            <KeyDisplay
              copiedKey={copiedKey}
              errorMessage={error || undefined}
              isGenerating={isGenerating}
              keyType="symmetric"
              keyTypeLabel="AES-256"
              title="Symmetric Key"
              value={generatedKeys?.symmetricKey || ''}
              onCopy={handleCopy}
              onDownload={handleDownload}
            />
          )}
        </div>

        <div className="flex justify-end">
          <Button
            color="primary"
            isDisabled={isGenerating || isExceeded}
            isLoading={isGenerating}
            radius="sm"
            onPress={handleGenerate}
          >
            {isGenerating ? 'Generating Keys...' : 'Generate Keys'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default GenerateKeysScreen
